import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSoftphone } from "../context/SoftphoneContext";
import { supabase } from "../lib/supabase";
import { formatPhone, toE164 } from "../lib/phoneFormat";
import { invokeErrorMessage } from "../lib/edgeError";

/**
 * Guidance shown when a softphone-only rep's browser softphone can't
 * (re)connect at call time. Shared by DealCallText + CallHomeownerButton.
 */
export const SOFTPHONE_RECONNECT_HINT =
  "Your softphone isn't connected. Open the Softphone page (app.bardinroofing.com/softphone) in Chrome, tap Retry until it shows Ready (allow the microphone if asked), then try the call again.";

interface Props {
  dealId: string;
  homeownerName?: string | null;
  homeownerPhone?: string | null;
  /** Compact = icon-only buttons for tight rows. Default = labeled buttons. */
  compact?: boolean;
  /** Text-only = hide the Call button (used for Sam's drone-pilot cards). */
  textOnly?: boolean;
  /** Call-only = hide the Text button (the comms drawer has its own compose box). */
  callOnly?: boolean;
}

/**
 * Reusable Call + Text buttons for any deal card.
 * Call: bridges through Bardin Twilio number.
 * Text: opens inline composer, sends via twilio-send-sms.
 *
 * Pass `textOnly` to suppress the Call button — the drone pilot view uses
 * this so Sam can text but not call (homeowner shouldn't get an unexpected
 * call from the company line mid-flight).
 */
export default function DealCallText({
  dealId,
  homeownerName,
  homeownerPhone,
  compact = false,
  textOnly = false,
  callOnly = false,
}: Props) {
  const { member } = useAuth();
  const softphone = useSoftphone();
  const [calling, setCalling] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [textBody, setTextBody] = useState("");
  const [sending, setSending] = useState(false);

  if (!homeownerPhone) return null;

  // Softphone is "live" when the user is enrolled and the Device is registered.
  // When live, calls go out via the in-browser SDK instead of bridging through
  // the user's cell. Caller ID stays +14708238816 either way.
  const softphoneLive = softphone.isEnabled && (softphone.status === "ready" || softphone.status === "registering");
  // The cell bridge can only ring US numbers (Twilio geo permissions), so a
  // rep with an international or missing cell (e.g. Hennie's +27… SA number)
  // must go through the softphone. toE164 returns null for non-US numbers.
  const bridgeable = !!toE164(member?.phone);
  // For a softphone-only rep, a dropped (not-registered) softphone is revived
  // on demand inside softphone.call() instead of falling back to a bridge
  // that can never work (Aug 31, 2026 — Hennie's Call List error).
  const useSoftphonePath = softphoneLive || (softphone.isEnabled && !bridgeable);

  async function handleCall(e: React.MouseEvent) {
    e.stopPropagation();
    if (!homeownerPhone) return;

    const promptMsg = useSoftphonePath
      ? `Call ${homeownerName ?? "homeowner"} at ${formatPhone(homeownerPhone)}?\n\nThe call will ring through your laptop softphone.`
      : `Call ${homeownerName ?? "homeowner"} at ${formatPhone(homeownerPhone)}?\n\nYour phone will ring first, then we'll connect you.`;
    if (!window.confirm(promptMsg)) return;

    setCalling(true);
    try {
      if (useSoftphonePath) {
        try {
          await softphone.call({
            phoneNumber: homeownerPhone,
            dealId,
            displayName: homeownerName ?? null,
          });
        } catch (err) {
          // Revival attempt failed for a softphone-only rep — replace the
          // SDK's terse "Softphone unavailable" with actionable guidance.
          if (!softphoneLive) throw new Error(SOFTPHONE_RECONNECT_HINT);
          throw err;
        }
      } else {
        const { data, error } = await supabase.functions.invoke("twilio-start-call", {
          body: { deal_id: dealId, rm_id: member?.id ?? null },
        });
        if (error) throw new Error(await invokeErrorMessage(error, "Call failed"));
        if (!data?.ok) throw new Error(data?.error || "Call failed");
      }
    } catch (err) {
      alert("Couldn't start call: " + (err as Error).message);
    } finally {
      setCalling(false);
    }
  }

  async function handleSend(e: React.MouseEvent) {
    e.stopPropagation();
    if (!homeownerPhone || !textBody.trim() || sending) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: homeownerPhone, body: textBody.trim(), deal_id: dealId, tag: "portal_quick", sent_by: member?.id ?? null },
      });
      if (error) throw new Error(error.message || "Send failed");
      if (!data?.ok) throw new Error(data?.error || "Send failed");
      setTextBody("");
      setTextOpen(false);
    } catch (err) {
      alert("Couldn't send text: " + (err as Error).message);
    } finally {
      setSending(false);
    }
  }

  if (compact) {
    return (
      <div className="relative flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        {!textOnly && (
          <button
            onClick={handleCall}
            disabled={calling}
            className="px-2 py-1 rounded text-[10px] font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50"
            title={`Call ${homeownerName ?? "homeowner"}`}
          >
            {calling ? "…" : "📞"}
          </button>
        )}
        {!callOnly && (
          <button
            onClick={(e) => { e.stopPropagation(); setTextOpen(!textOpen); }}
            className="px-2 py-1 rounded text-[10px] font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition"
            title={`Text ${homeownerName ?? "homeowner"}`}
          >
            💬
          </button>
        )}
        {textOpen && !callOnly && (
          <div className="absolute right-0 top-full mt-1 z-10 bg-white border border-slate-200 rounded-lg shadow-lg p-2 flex gap-1.5 min-w-[240px]" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={textBody}
              onChange={(e) => setTextBody(e.target.value)}
              placeholder={`Text ${homeownerName ?? "homeowner"}…`}
              className="flex-1 border border-slate-300 rounded px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-teal-500"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleSend(e as any); if (e.key === "Escape") setTextOpen(false); }}
            />
            <button
              onClick={handleSend}
              disabled={sending || !textBody.trim()}
              className="px-2 py-1 rounded bg-teal-500 text-white text-[11px] font-semibold hover:bg-teal-600 disabled:opacity-50 transition"
            >
              {sending ? "…" : "Send"}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="flex gap-1.5">
        {!textOnly && (
          <button
            onClick={handleCall}
            disabled={calling}
            className="px-3 py-1.5 rounded-md bg-white text-slate-600 border border-slate-200 text-[11px] font-semibold hover:bg-slate-50 transition disabled:opacity-50 inline-flex items-center gap-1"
            title={`Call ${formatPhone(homeownerPhone)}`}
          >
            📞 {calling ? "Dialing…" : "Call"}
          </button>
        )}
        {!callOnly && (
          <button
            onClick={(e) => { e.stopPropagation(); setTextOpen(!textOpen); }}
            className="px-3 py-1.5 rounded-md bg-white text-slate-600 border border-slate-200 text-[11px] font-semibold hover:bg-slate-50 transition inline-flex items-center gap-1"
            title={`Text ${formatPhone(homeownerPhone)}`}
          >
            💬 Text
          </button>
        )}
      </div>
      {textOpen && !callOnly && (
        <div className="flex gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            placeholder={`Text ${homeownerName ?? "homeowner"}…`}
            className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-500"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") handleSend(e as any); if (e.key === "Escape") setTextOpen(false); }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !textBody.trim()}
            className="px-3 py-1.5 rounded-md bg-teal-500 text-white text-[11px] font-semibold hover:bg-teal-600 disabled:opacity-50 transition"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}
